/*  ========================================================================
    # Components - Image Slider
    ========================================================================  */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import { theme } from '../../../../tailwind.config';
import bem from '../../utils/bem';

// Components
import LazyImage from '../LazyImage';
import LazySource from '../LazySource';

const ImageSlider = ({ classes, entries, onClickImage, squareImages }) => {
  const [images, setImages] = useState(entries);
  const [activeSlide, setActiveSlide] = useState(0);

  /**
   *
   */
  if (images.length > 1 && images.length < 3) {
    const clonedImages = [...images];
    while (clonedImages.length < 3) {
      clonedImages.push(...images);
    }
    setImages(clonedImages);
  }

  /**
   * Navigate the slider to the left.
   */
  const navigateLeft = () => {
    if (activeSlide - 1 < 0) {
      setActiveSlide(images.length - 1);
    } else {
      setActiveSlide(activeSlide - 1);
    }
  };

  /**
   * Navigate the slider to the right.
   */
  const navigateRight = () => {
    if (activeSlide + 2 > images.length) {
      setActiveSlide(0);
    } else {
      setActiveSlide(activeSlide + 1);
    }
  };

  // Create handlers for swipe recognition.
  const swipeHandlers = useSwipeable({
    onSwipedLeft: navigateRight,
    onSwipedRight: navigateLeft
  });

  /**
   * Get Image Offset.
   */
  const getImageOffset = index => {
    if (index === activeSlide) return 0;
    else if (index + 2 <= activeSlide) return -2;
    else if (index - 2 >= activeSlide) return 2;
    else if (index < activeSlide) return -1;
    else return 1;
  };

  /**
   * Reset the active slide index to 0 on windows resize.
   */
  useEffect(() => {
    window.addEventListener(
      'resize',
      () => {
        setActiveSlide(0);
      },
      { passive: true }
    );
  }, []);

  return (
    <div className={`c-image-slider ${classes}`}>
      {images.length > 1 ? (
        <button
          className='c-image-slider__arrow c-image-slider__arrow--left'
          onClick={() => navigateLeft()}
        />
      ) : null}
      <div className='c-image-slider__wrapper' {...swipeHandlers}>
        <div className='c-image-slider__axis'>
          {images.map((image, index) => (
            <div
              key={`${image.image.title}-${index}`}
              className={bem('c-image-slider', 'entry', { square: squareImages })}
              data-offset={getImageOffset(index)}
            >
              <LazyImage
                defaultUrl={image.image.sizes.medium}
                placeholderUrl={image.image.sizes.placeholder}
                alt={image.image.alt}
                caption={image.caption}
                credits={image.show_credits ? image.credits : ''}
                onClick={onClickImage ? () => onClickImage(image) : null}
                preload={true}
                figureStyle={{ aspectRatio: image.width + '/' + image.height }}
                focusX={image.image.focus?.x}
                focusY={image.image.focus?.y}
              >
                <LazySource srcSet={image.image.sizes.large} mediaMin={theme.screens.sm} />
              </LazyImage>
            </div>
          ))}
        </div>
      </div>
      {images.length > 1 ? (
        <button
          className='c-image-slider__arrow c-image-slider__arrow--right'
          onClick={() => navigateRight()}
        />
      ) : null}
    </div>
  );
};

ImageSlider.propTypes = {
  classes: PropTypes.string,
  entries: PropTypes.array,
  onClickImage: PropTypes.func,
  squareImages: PropTypes.bool
};

export default ImageSlider;
